import React, { useEffect, useRef } from "react";
import './CommonDialog.scss';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Box,
    Button,
    Avatar,
} from "@mui/material";

export interface CommonDialogProps {
    open: boolean;
    title: string;
    content?: React.ReactNode | string;
    confirmText?: string;
    cancelText?: string;
    onConfirm?: () => void;
    onClose: () => void;
    showActions?: boolean;
    showCancel?: boolean;
    disableEscapeKeyDown?: boolean;
    centerActions?: boolean;
    imageSrc?: string;
    imageAlt?: string;
    titleAlign?: "center" | "left";
    dialogClassName?: string;
    confirmButtonClassName?: string;
    cancelButtonClassName?: string;
}

const CommonDialog: React.FC<CommonDialogProps> = ({
                                                       open,
                                                       title,
                                                       content,
                                                       confirmText = "OK",
                                                       cancelText = "Cancel",
                                                       onConfirm,
                                                       onClose,
                                                       showActions = true,
                                                       showCancel = true,
                                                       disableEscapeKeyDown = false,
                                                       centerActions = false,
                                                       imageSrc,
                                                       imageAlt = "guardian-avatar",
                                                       titleAlign = "left",
                                                       dialogClassName,
                                                       confirmButtonClassName,
                                                       cancelButtonClassName,
                                                   }) => {
    // 1. Tạo ref để truy cập trực tiếp vào DOM Container của Dialog
    const dialogRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (open) {
            // Delay siêu ngắn (150ms) để đảm bảo Material UI đã hoàn tất việc render CSS transition/animation
            const timer = setTimeout(() => {
                const dialogDOM = dialogRef.current;
                if (dialogDOM) {
                    // Cách 1: Tạo một thay đổi DOM nhỏ bằng việc set Attribute ngẫu nhiên (Giúp kích thích bộ dịch quét lại)
                    dialogDOM.setAttribute("data-force-translate", Math.random().toString());

                    // Cách 2: Giả lập sự kiện mouseenter ngay tại container của dialog
                    const hoverEvent = new MouseEvent("mouseenter", {
                        bubbles: true,
                        cancelable: true,
                        view: window,
                    });
                    dialogDOM.dispatchEvent(hoverEvent);
                }
            }, 150);

            return () => clearTimeout(timer);
        }
    }, [open, content, title]); // Chạy lại nếu nội dung hoặc trạng thái đóng/mở thay đổi

    return (
        <Dialog
            ref={dialogRef} // 2. Gán ref vào Dialog component
            open={open}
            onClose={onClose}
            className={`${dialogClassName} dialog-container`}
            disableEscapeKeyDown={disableEscapeKeyDown}
        >
            {/* Tiêu đề Dialog */}
            <DialogTitle>
                <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    justifyContent={titleAlign === "center" ? "center" : "flex-start"}
                >
                    {imageSrc && (
                        <Avatar
                            className="dialog-avatar"
                            src={imageSrc}
                            alt={imageAlt}
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: "50%",
                                boxShadow: "0 0 8px rgba(0,0,0,0.3)",
                            }}
                        />
                    )}
                    <Typography
                        variant="h6"
                        component="span"
                        sx={{
                            fontWeight: "bold",
                            color: "#414335",
                            fontSize: { lg: '1.2rem', md: "1.15rem", xs: "1.15rem" },
                        }}
                    >
                        {title}
                    </Typography>
                </Box>
            </DialogTitle>

            {/* Nội dung */}
            <DialogContent sx={{ fontSize: "1.15rem" }}>
                {/* Nút Cancel ẩn danh này có vẻ là nguyên nhân gây ra trigger hover lúc trước.
                  Bạn có thể giữ nguyên hoặc xóa nếu nó là thừa.
                */}
                <Button
                    id={"myButton"}
                    onClick={onClose}
                    className={cancelButtonClassName}
                    sx={{
                        textTransform: "none",
                        fontSize: { lg: '1.15rem', md: "0.875rem" },
                        display: "none" // Ẩn nút thừa này đi nếu chỉ dùng để debug
                    }}
                >
                    {cancelText}
                </Button>

                {typeof content === "string" ? (
                    <Typography>{content}</Typography>
                ) : (
                    content
                )}

                {/* Nút hành động */}
                {showActions && (
                    <Box
                        mt={2}
                        display="flex"
                        justifyContent={centerActions ? "center" : "flex-end"}
                        gap={2}
                    >
                        {showCancel && cancelText && (
                            <Button
                                onClick={onClose}
                                className={cancelButtonClassName}
                                sx={{
                                    textTransform: "none",
                                    fontSize: { lg: '1.15rem', md: "0.875rem" }
                                }}
                            >
                                {cancelText}
                            </Button>
                        )}

                        {onConfirm && (
                            <Button
                                variant="contained"
                                onClick={onConfirm}
                                className={confirmButtonClassName}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: "bold",
                                    fontSize: { lg: '1.15rem', md: "0.875rem" }
                                }}
                            >
                                {confirmText}
                            </Button>
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default CommonDialog;